/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2018
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'jquery',
	'underscore',
	'../../lib/@waca/tabbable/index',
	'../../nls/StringResources',
	'../../utils/DialogBlocker'
], function(Class, $, _, tabbable, stringResources, DialogBlocker) {


	var Dialog = Class.extend({
		_buttons: ['ok', 'cancel'],
		_showCloseX: false,
		_width: null,
		_minWidth:null,

		init: function( options ) {
			this._dialogId = _.uniqueId('modalDialog_');
			this._queryId = '#' + this._dialogId;
			this._defaultKeyMap = { '13': this.ok, '27': this.cancel };
			this.keyMap = this._getKeyCodeMap();
			this._blocker = null;

			// set dialog options
			this.setDialogOptions( options );
		},

		setDialogOptions: function( options ){
			// process options
			if( options ) {

				// set width as necessary
				if( options.width ) {
					this._width = options.width;
				}

				if(options.minWidth){
					this._minWidth = options.minWidth;
				}

				// set closeX button option
				if( options.showCloseX ) {
					this._showCloseX = options.showCloseX;
				}

				if( options.zindex != null) {
					this._zindex = options.zindex;
				}

				// Check for buttons passed in and override defaults.
				if( options.buttons ) {
					this._buttons = options.buttons;
				}
			}
		},

		destroy: function() {
			this._removeTabListeners();
			this._removeButtonListeners();
			$(this._queryId).remove();
			if (this._blocker) {
				this._blocker.remove();
				this._blocker = null;
			}
		},

		remove: function() {
			this.destroy();
		},

		hide: function() {
			$(this._queryId).removeClass('show');
			this.destroy();
		},

		open: function() {
			this.showBlocker();
			this.show();
			this._limitTabToDialog();
		},

		// Children can override to provide different blocker cell definitions to use
		getBlockerCell: function() {
			return $('<div>', {
				'id': this._dialogId + 'Container',
				'class': 'dialogBlockerCell'
			});
		},

		showBlocker: function() {
			let nBlocker = $(this._queryId);
			if (!nBlocker.length) {
				nBlocker = DialogBlocker.show(document.body);
				nBlocker.attr('id', this._dialogId);
				nBlocker.addClass('show');

				// set the blocker height (works with scrollbars)
				nBlocker.height($(document).height());
				if(this._zindex){
					nBlocker.css('z-index', this._zindex);
				}
				const nBlockerCell = this.getBlockerCell();

				nBlocker.append(
					nBlockerCell.text( stringResources.get('dlg_loading') )
				);
				nBlocker.on('keydown', this.onKey.bind(this));
			}
			this._blocker = nBlocker;
		},

		show: function() {
			var nContainer = this._container();

			// set width as necessary
			if( this._width ) {
				nContainer.css('max-width', this._width );
			}

			if( this._minWidth ) {
				nContainer.css('min-width', this._minWidth );
			}

			// build dialog as necessary
			if (nContainer.length) {
				nContainer.empty();

				var header = $('<div>', { 'class': 'dialogHeader' }),
					title = this.renderTitle( $('<header>', { 'class': 'dialogTitle' }) ),
					closeX = this.renderCloseX(),
					content = this.renderContent( $('<div>', { 'class': 'dialogContent' }) ),
					footer = this.renderFooter( $('<footer>') );

				// pop the title and closeX into the header
				header.append( title, closeX );

				// set width as necessary (this prevents content from collapsing)
				if( this._width ) {
					header.css( 'max-width',this._width );
				}

				if( this._minWidth ) {
					header.css( 'min-width',this._minWidth );
				}

				// assemble dialog
				nContainer.append(
					$('<div>', {
						'class': 'modalDialog',
						'tabIndex': '0',
						'role': 'dialog'
					}).append(
						header,
						content,
						footer
					)
				);

				//Support for IE
				var modalDialog = nContainer.find('.modalDialog');

				if(this._width){
					modalDialog.css('max-width', this._width);
				}
				if(this._minWidth){
					modalDialog.css('min-width', this._minWidth);
				}

				this.focusOnButton();
			}
		},

		// Children can override to provide different initial button focusing behaviour
		focusOnButton: function() {
			this._container()
				.find('footer')
				.children('.dialogButton')
				.first()
				.focus();
		},

		renderContent: function(n) {
			n.text('Dialog Content');
			return n;
		},

		renderTitle: function(n) {
			n.text('Dialog Title');
			return n;
		},

		/**
		 *	This function adds a CSS class that will cause the dialog to be centered vertically.
		 *	This could potentially be affected by additional styles in extended dialog classes.
		 **/
		verticalAlignDialog: function() {
			this._container().addClass( 'dialogVerticalAlign' );
		},

		renderCloseX: function() {
			// only render if required
			if( !this._showCloseX ){
				return null;
			}

			// create button
			var closeX = $('<div>',
				{
					'class': 'wfg_shape_x dialogCloseX',
					'title': stringResources.get('dlg_close'),
					'aria-label': stringResources.get('dlg_close'),
					'role': 'button'
				});
			closeX.on('click', this.cancel.bind( this ));
			return closeX;
		},

		renderFooter: function(n) {

			// loop though button collection and process
			for (var i = 0, iLen = this._buttons.length; i < iLen; i++) {
				var btnSpec = this._getButtonDef( this._buttons[i] );
				var b = {
					'class': 'dialogButton ' + btnSpec.type,
					'text': btnSpec.text,
					'aria-label' : btnSpec.text,
					'role' : 'button',
					'tabIndex': '0'
				};
				n.append($('<button>', b).on('click', btnSpec.handler.bind(this)));
			}

			return n;
		},

		_removeButtonListeners: function() {
			var nContainer = this._container();
			nContainer.find('div.dialogCloseX').off('click');
			nContainer.find('footer button').off('click');
		},

		onKey: function(evt) {
			var keyCodeString = evt.keyCode.toString();

			if (this.keyMap.hasOwnProperty(keyCodeString)) {
				this.keyMap[keyCodeString].call(this);
				return false;
			}

			return true;
		},

		ok: function() {
			this.hide();
		},

		cancel: function() {
			this.hide();
		},

		enableOk: function(bEnabled) {
			var btn = $('.dialogButton', this._container()).eq(0);
			btn.toggleClass('disabled', bEnabled === false); // if bEnabled is undefined, it means true.
			if (bEnabled === false) {
				btn.attr('disabled','disabled');
			} else {
				btn.removeAttr('disabled');
			}
		},

		disableOk: function() {
			this.enableOk(false);
		},

		// Private

		// Children can override to provide different key event mappings
		_getKeyCodeMap: function() {
			return this._defaultKeyMap;
		},

		_limitTabToDialog: function() {
			this._removeTabListeners();

			var tabbables = this._getTabbables();
			if (tabbables.length > 1) {
				var $first = $(tabbables[1]);
				var $last = $(tabbables[tabbables.length - 1]);

				this._$firstTabbale = $first;
				this._$lastTabbable = $last;

				$last.on('keydown.modal', (e) => {
					if ((e.which === 9 && !e.shiftKey)) {
						e.preventDefault();
						$first.focus();
					}
				});

				$first.on('keydown.modal', (e) => {
					if ((e.which === 9 && e.shiftKey)) {
						e.preventDefault();
						$last.focus();
					}
				});
			}
		},

		_container: function() {
			return $(this._queryId + 'Container');
		},

		_getTabbables: function() {
			return tabbable(this._container()[0]);
		},

		_removeTabListeners: function() {
			if( this._$firstTabbale)  {
				this._$firstTabbale.off('keydown.modal');
				this._$firstTabbale = null;
			}
			if( this._$lastTabbable ) {
				this._$lastTabbable.off('keydown.modal');
				this._$lastTabbable = null;
			}
		},

		/**
		 *	This function takes a given button id or partial definition and updates it to return a complete definition.
		 *
		 *	A button can be an id such as: 'ok', 'cancel', or 'close' OR
		 *	a button can be an object containing one or more of the following options:
		 *			{
		 *				text: 		String label to display
		 *				handler: 	Callback function
		 *				defaultId:  If specified this will use the spec specified by the id (i.e. 'ok') for missing info
		 *				type: 		'primary' or 'secondary' will set the class and visuals accordingly. 'destructive'
		 *							is not currently supportted but might be in the future
		 *			}
		 *
		 *	@param		button Button id or partial spec object
		 *  @returns	Object Complete button spec.
		 **/
		_getButtonDef: function( button ) {
			// define the default button spec
			var buttonsDef = {
				'ok': { 'text': stringResources.get('dlg_ok'), 'handler': this.ok, 'type':'primary' },
				'cancel': { 'text': stringResources.get('dlg_cancel'), 'handler': this.cancel, 'type':'secondary' },
				'close': { 'text': stringResources.get('dlg_close'), 'handler': this.cancel, 'type':'primary' }
			};

			// if object is passed then fill in missing otherwise lookup and return
			if( typeof( button ) === 'object' ) {
				// check for defaultId
				if( button.defaultId ) {
					// default text if necessary
					if( !button.text ) {
						button.text = buttonsDef[ button.defaultId ].text;
					}

					// default handler if necesssary
					if( !button.handler ) {
						button.handler = buttonsDef[ button.defaultId ].handler;
					}

					// default type if necesssary
					if( !button.type ) {
						button.type = buttonsDef[ button.defaultId ].type;
					}
				}

				// return
				return button;
			}

			return buttonsDef[ button ];
		}
	});

	return Dialog;
});
